import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DisplayUploadFileComponent } from '@aid/shared/components/display-upload-file/display-upload-file.component';

@Component({
  selector: 'aid-upload-file',
  template: `
    <input
      type="file"
      #file
      style="display: none"
      (change)="onAddFile($event, file.files)"
      [accept]="acceptFiles"
    />
    <aid-display-upload-file
      [displayedUrl]="displayedUrl"
      [defaultUrl]="defaultUrl"
      [showDefault]="showDefault"
      [horizontalSize]="horizontalSize"
      [verticalSize]="verticalSize"
      [details]="details"
      [title]="title"
      [width]="width"
      [height]="height"
      (upload)="onUploadPhoto()"
      (cancelPhoto)="onClose()"
    ></aid-display-upload-file>

    @if (!willCropPhoto && !isSizeValid) {
      <mat-error class="mat-small" style="font-weight:600">
        *{{ 'size-error' | translate | sentancecase }} {{ horizontalSize }}x{{
          verticalSize
        }}px
      </mat-error>
    }
  `,
  styleUrls: ['./upload-file.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UploadFileComponent implements OnInit {
  @Input() defaultUrl?: string;
  @Input() willCropPhoto = false;
  @Input() acceptFiles?: string;
  @Input({ required: true }) control = new UntypedFormControl();
  @Input() horizontalSize?: number;
  @Input() details?: string;
  @Input() title?: string;
  @Input() verticalSize?: number;
  @ViewChild('file') file?: ElementRef<HTMLInputElement>;

  @ViewChild(DisplayUploadFileComponent)
  clickToUpload?: DisplayUploadFileComponent;
  width?: number | null;
  height?: number | null;
  displayedUrl?: string | null;
  showDefault = true;

  constructor(private cdRef: ChangeDetectorRef) {}

  ngOnInit() {}

  onUploadPhoto() {
    this.cdRef.detectChanges();
    this.fileInput.click();
  }

  // The cropper consumes the change event itself; otherwise only the file matters.
  onAddFile(event: Event, files: FileList | null) {
    if (this.willCropPhoto) {
      this.control.setValue(event);
      return;
    }
    const photo = files?.[0];
    if (photo) {
      const reader = new FileReader();
      reader.readAsDataURL(photo);

      reader.onloadend = () => {
        this.displayedUrl = reader.result as string;
        this.showDefault = true;

        if (!this.willCropPhoto) {
          const img = new Image();
          img.src = reader.result as string;

          img.onload = (e: any) => {
            this.width = e.path[0].width;
            this.height = e.path[0].height;

            this.cdRef.detectChanges();

            if (this.isSizeValid) {
              this.control.setValue(photo);
            }
          };
        }
      };
    }
    this.cdRef.detectChanges();
  }

  onCancel() {
    this.onClose();
    this.showDefault = true;
  }

  onClose() {
    this.fileInput.value = '';
    this.control.setValue(null);
    this.displayedUrl = null;
    this.showDefault = false;
    this.width = null;
    this.height = null;
  }

  get isSizeValid() {
    if (!this.width && !this.height) {
      return true;
    }
    return (
      this.width === this.horizontalSize && this.height === this.verticalSize
    );
  }

  private get fileInput(): HTMLInputElement {
    if (!this.file) {
      throw new Error('aid-upload-file: the file input is not rendered.');
    }
    return this.file.nativeElement;
  }
}
